// src/components/desk/contract/RentalOpening.jsx — the four questions asked before the lease form
// (owner 2026-09-19). The rules live in src/lib/rentalOpening.js; this only draws them, in the
// same choice buttons RentalPaymentPlan uses, one column so a phone never scrolls sideways.
import { t } from '../../../lib/i18n.js'
import { OPENING_METHODS, methodAllowed, sameAllowed, openingMissing } from '../../../lib/rentalOpening.js'

const CHOICE = 'w-full text-left rounded border px-2.5 py-1.5 text-[13px] leading-snug transition-colors break-words'
const ON = 'border-[#1F6F4F] bg-[#1F6F4F]/10 text-[#1F6F4F]'
const OFF = 'border-[#B9B6AD] text-[#14130F] hover:border-[#14130F]/40'
const OFF_DISABLED = 'border-[#B9B6AD]/60 text-[#14130F]/40 cursor-not-allowed'

const METHOD_KEY = { cash: 'cash', instant_transfer: 'instant', local_bank_transfer: 'local', international_wire: 'swift', other: 'other' }

function Question({ id, title, help, children }) {
  return (
    <section className="space-y-1.5 min-w-0" data-field={`opening:${id}`} data-opening-question={id}>
      <div className="text-[13px] font-medium">{title}</div>
      <div className="flex flex-col gap-1.5" role="radiogroup" aria-label={title}>{children}</div>
      {help && <div className="text-[11px] text-[#14130F]/60">{help}</div>}
    </section>
  )
}

function Choice({ on, disabled, onClick, children, note, testid }) {
  return (
    <button type="button" role="radio" aria-checked={!!on} aria-disabled={disabled || undefined} data-opening-choice={testid}
      className={`${CHOICE} ${on ? ON : disabled ? OFF_DISABLED : OFF}`} onClick={disabled ? undefined : onClick}>
      <span className="block">{children}</span>
      {note && <span className="block text-[11px] text-[#14130F]/60 mt-0.5">{note}</span>}
    </button>
  )
}

export function RentalOpening({ questions, answers, onAnswer, onDone }) {
  const a = answers || {}
  const missing = openingMissing(a, questions)
  const has = q => questions.includes(q)
  return (
    <div className="space-y-4 min-w-0 max-w-full" data-testid="rental-opening">
      <div className="text-[14px] font-semibold">{t('desk_rental_open_title')}</div>

      <Question id="inspection" title={t('desk_rental_open_q1')}>
        <Choice testid="in_person" on={a.inspection === 'in_person'} onClick={() => onAnswer('inspection', 'in_person')}>{t('desk_rental_open_q1_in_person')}</Choice>
        <Choice testid="remote_inspected" on={a.inspection === 'remote_inspected'} onClick={() => onAnswer('inspection', 'remote_inspected')}>{t('desk_rental_open_q1_remote_inspected')}</Choice>
        <Choice testid="remote_media" on={a.inspection === 'remote_media'} onClick={() => onAnswer('inspection', 'remote_media')}>{t('desk_rental_open_q1_remote_media')}</Choice>
      </Question>

      {has('handover') && (
        <Question id="handover" title={t('desk_rental_open_q2')} help={t('desk_rental_open_q2_help')}>
          <Choice testid="tenancy_start" on={a.handover === 'tenancy_start'} onClick={() => onAnswer('handover', 'tenancy_start')}>{t('desk_rental_open_q2_start')}</Choice>
          <Choice testid="later_by_arrangement" on={a.handover === 'later_by_arrangement'} onClick={() => onAnswer('handover', 'later_by_arrangement')}>{t('desk_rental_open_q2_later')}</Choice>
        </Question>
      )}

      <Question id="method" title={t('desk_rental_open_q3')}>
        {OPENING_METHODS.map(m => {
          const ok = methodAllowed(m, a.inspection)
          const k = METHOD_KEY[m]
          return (
            <Choice key={m} testid={m} on={a.method === m} disabled={!ok} onClick={() => onAnswer('method', m)}
              note={!ok ? t('desk_rental_open_cash_remote_no') : m === 'other' ? null : t(`desk_rental_open_m_${k}_note`)}>
              {t(`desk_rental_open_m_${k}`)}
            </Choice>
          )
        })}
      </Question>

      <Question id="recurring" title={t('desk_rental_open_q4')}>
        <Choice testid="same_as_initial" on={a.recurring === 'same_as_initial'} disabled={!sameAllowed(a.method)}
          note={!sameAllowed(a.method) ? t('desk_rental_open_same_other_no') : null}
          onClick={() => onAnswer('recurring', 'same_as_initial')}>{t('desk_rental_open_r_same')}</Choice>
        <Choice testid="specified_in_contract" on={a.recurring === 'specified_in_contract'} onClick={() => onAnswer('recurring', 'specified_in_contract')}>{t('desk_rental_open_r_spec')}</Choice>
      </Question>

      <div className="flex flex-wrap items-center gap-2 pt-1">
        <button type="button" data-testid="rental-opening-continue" disabled={missing.length > 0} onClick={onDone}
          className="rounded-md bg-[#1F6F4F] px-3 py-1.5 text-[13px] text-white disabled:opacity-40">{t('desk_rental_open_continue')}</button>
        {missing.length > 0 && <span className="text-[11px] text-[#14130F]/60">{t('desk_rental_open_answer_all')}</span>}
      </div>
    </div>
  )
}

/** One line over the payment block once the opening is answered, with a way back to it. */
// `only` (2026-09-23, owner: "separate remote signing and key handover"): each block of the form shows the
// answers it owns — signing shows the inspection answer, handover the keys answer, payment the method ones.
export function RentalOpeningSummary({ answers, questions, onChange, only = null }) {
  const a = answers || {}
  const want = k => !only || only.includes(k)
  const parts = [
    want('inspection') ? (a.inspection === 'remote_inspected' ? t('desk_rental_open_sum_remote_inspected')
      : a.inspection === 'remote_media' ? t('desk_rental_open_sum_remote_media')
      : t('desk_rental_open_sum_in_person')) : null,
    want('handover') && questions.includes('handover') && a.handover ? t(a.handover === 'tenancy_start' ? 'desk_rental_open_q2_start' : 'desk_rental_open_q2_later') : null,
    want('method') && a.method && METHOD_KEY[a.method] ? t(`desk_rental_open_m_${METHOD_KEY[a.method]}`) : null,
    want('recurring') && a.recurring ? t(a.recurring === 'same_as_initial' ? 'desk_rental_open_sum_same' : 'desk_rental_open_sum_spec') : null,
  ].filter(Boolean)
  if (!parts.length) return null
  return (
    <div className="rounded border border-[#14130F]/12 bg-[#F7F5F0] px-2.5 py-1.5 text-[12px] flex flex-wrap items-center gap-x-2 gap-y-1 min-w-0" data-testid="rental-opening-summary">
      <span className="min-w-0 break-words">{parts.join(' · ')}</span>
      <button type="button" className="underline decoration-dotted text-[#1F6F4F] shrink-0" onClick={onChange}>{t('desk_rental_open_change')}</button>
    </div>
  )
}
